class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());
    this.querySelector("#CartDrawer-Overlay").addEventListener("click", this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add("animate", "active");
      const cartDrawerItems = this.querySelector("cart-drawer-items");
      if (cartDrawerItems) {
        cartDrawerItems.updateCartUpsellToggleState();
        cartDrawerItems.updateCartUpsellVisibility();
      }
      document.dispatchEvent(new CustomEvent("cart:open"));

      // ✅ Free gift check when drawer opens (safe, debounced)
      if (window.FreeGiftThresholdManager && window.FreeGiftThresholdManager.queueCheck) {
        window.FreeGiftThresholdManager.queueCheck("drawer-open");
      }
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement = this.querySelector(".drawer__inner") || this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute("aria-controls", cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener("click", this.close.bind(this));
      this.open();

      // ✅ Free gift check after drawer re-render
      if (window.FreeGiftThresholdManager && window.FreeGiftThresholdManager.queueCheck) {
        window.FreeGiftThresholdManager.queueCheck("drawer-renderContents");
      }
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: "cart-drawer", selector: "#CartDrawer" },
      { id: "cart-icon-bubble" },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: "CartDrawer", section: "cart-drawer", selector: ".drawer__inner" },
      { id: "cart-icon-bubble", section: "cart-icon-bubble", selector: ".shopify-section" },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

/* =========================================================
   ✅ Free Gift Threshold Manager (stable + no spam + no loops)
   - runs on real cart mutations only (add/change/update/clear)
   - ignores attribute update calls
   - ignores its own requests using X-FreeGift header
   - removes ALL gift lines when below threshold
   - enforces EXACTLY 1 gift when above threshold
========================================================= */
(function () {
  if (window.FreeGiftThresholdManager) return;

  const DEBUG = false; // <- set true only when debugging

  const log = (...args) => DEBUG && console.log("[FreeGift]", ...args);
  const warn = (...args) => console.warn("[FreeGift]", ...args);

  const manager = {
    busy: false,
    timer: null,
    lastRun: 0,

    queueCheck(reason = "") {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.run(reason), 180);
    },

    getSettings() {
      const drawer = document.querySelector("cart-drawer");
      if (!drawer) return null;

      const freeVariantId = Number(drawer.dataset.freeVariantId || 0);
      const thresholdCents = Number(drawer.dataset.thresholdCents || 0);

      if (!freeVariantId || !thresholdCents) {
        warn("Missing data-free-variant-id or data-threshold-cents on <cart-drawer>", drawer?.dataset);
        return null;
      }

      return { freeVariantId, thresholdCents };
    },

    async fetchCart() {
      const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error("Failed to fetch /cart.js");
      return await res.json();
    },

    getGiftLines(cart, freeVariantId) {
      return (cart.items || []).filter((it) => Number(it.variant_id) === Number(freeVariantId));
    },

    async changeLineByKey(key, qty) {
      // IMPORTANT: add header so watcher can ignore this request (prevents loops)
      const res = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-FreeGift": "1",
        },
        body: JSON.stringify({ id: key, quantity: qty }),
      });
      if (!res.ok) throw new Error("Failed /cart/change.js");
      return await res.json();
    },

    async addGiftVariant(freeVariantId) {
      // IMPORTANT: add header so watcher can ignore this request (prevents loops)
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-FreeGift": "1",
        },
        body: JSON.stringify({
          id: freeVariantId,
          quantity: 1,
          properties: { _auto_free_gift: "true" },
        }),
      });

      if (!res.ok) {
        let msg = "";
        try {
          const j = await res.json();
          msg = j?.description || j?.message || "";
        } catch (e) {}
        throw new Error(msg || "Failed to add gift");
      }
      return await res.json();
    },

    async enforceExactlyOneGift(cart, freeVariantId) {
      const lines = this.getGiftLines(cart, freeVariantId);
      if (!lines.length) return;

      // Keep first line, remove others
      const keep = lines[0];
      const dupes = lines.slice(1);

      if (Number(keep.quantity) !== 1) {
        log("Fix gift qty -> 1", keep.key, keep.quantity);
        await this.changeLineByKey(keep.key, 1);
      }

      for (const d of dupes) {
        log("Remove duplicate gift line", d.key, d.quantity);
        await this.changeLineByKey(d.key, 0);
      }
    },

    async removeAllGift(cart, freeVariantId) {
      const lines = this.getGiftLines(cart, freeVariantId);
      if (!lines.length) return;

      for (const l of lines) {
        log("Removing gift line", l.key, l.quantity);
        await this.changeLineByKey(l.key, 0);
      }
    },

    refreshDrawerUI() {
      // Only refresh drawer UI once, do not publish custom cartUpdate loops
      const drawerItems = document.querySelector("cart-drawer-items");
      if (drawerItems && typeof drawerItems.onCartUpdate === "function") {
        drawerItems.onCartUpdate();
      }
    },

    async run(reason = "") {
      const settings = this.getSettings();
      if (!settings) return;

      // Avoid micro loops
      const now = Date.now();
      if (now - this.lastRun < 250) return;
      this.lastRun = now;

      if (this.busy) return;

      const { freeVariantId, thresholdCents } = settings;

      try {
        this.busy = true;

        const cart = await this.fetchCart();
        const subtotal = Number(cart.items_subtotal_price || 0);
        const shouldHaveGift = subtotal >= thresholdCents;

        log("RUN", { reason, subtotal, thresholdCents, shouldHaveGift });

        const giftLines = this.getGiftLines(cart, freeVariantId);

        if (shouldHaveGift) {
          if (!giftLines.length) {
            log("Adding gift...");
            await this.addGiftVariant(freeVariantId);
            const cart2 = await this.fetchCart();
            await this.enforceExactlyOneGift(cart2, freeVariantId);
          } else {
            await this.enforceExactlyOneGift(cart, freeVariantId);
          }
        } else {
          // BELOW threshold => REMOVE all gift lines (variant id match)
          await this.removeAllGift(cart, freeVariantId);
        }

        // ✅ Refresh UI only once after operations
        this.refreshDrawerUI();
      } catch (e) {
        warn("Error:", e?.message || e);
      } finally {
        this.busy = false;
      }
    },
  };

  window.FreeGiftThresholdManager = manager;

  /* =========================================================
     ✅ Fetch watcher (single install, no spam, no loops)
     - only triggers on real cart mutations
     - ignores /cart/update.js?attributes...
     - ignores requests with X-FreeGift header
  ========================================================= */
  if (!window.__FreeGiftFetchWatcherInstalled) {
    window.__FreeGiftFetchWatcherInstalled = true;

    const originalFetch = window.fetch;

    window.fetch = async function (...args) {
      const input = args[0];
      const init = args[1] || {};

      const url =
        typeof input === "string"
          ? input
          : input && input.url
          ? input.url
          : "";

      // ignore if our own request
      const headers = init.headers || {};
      const isFreeGiftRequest =
        (headers && (headers["X-FreeGift"] === "1" || headers["x-freegift"] === "1")) ||
        (headers instanceof Headers && headers.get("X-FreeGift") === "1");

      const res = await originalFetch.apply(this, args);

      try {
        if (!url) return res;

        // Only react to actual cart mutations, not attributes/locale updates
        const isCartMutation =
          url.includes("/cart/add") ||
          url.includes("/cart/change") ||
          url.includes("/cart/clear") ||
          (url.includes("/cart/update") && !url.includes("attributes"));

        // Must be POST-ish (or empty method in some theme calls)
        const method = (init.method || (input && input.method) || "").toUpperCase();
        const postLike = !method || method === "POST";

        if (!isFreeGiftRequest && isCartMutation && postLike && res && res.ok) {
          // Debounced check
          manager.queueCheck("fetch-cart-mutation");
        }
      } catch (e) {}

      return res;
    };
  }
})();
